import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { ApiUrls } from '../../../constants/api-urls';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Subscription } from 'rxjs';

type Strength = 'Empty' | 'Weak' | 'Fair' | 'Good' | 'Strong';

interface SelectedPlanInfo {
  planName: string;
  billingTerm: string;
  pricePerMonth: number;
  currencySymbol: string;
  quotaCalls: number | null;
}

const SELECTED_PLAN_KEY = 'icip_selected_plan';

// Custom validator to prevent whitespace-only passwords
function noWhitespaceOnlyValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }
  const isWhitespaceOnly = /^\s+$/.test(control.value);
  return isWhitespaceOnly ? { whitespaceOnly: true } : null;
}

@Component({
  selector: 'app-register',
  standalone: false,
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit, OnDestroy {
  captchaToken: string | null = null;
  registerForm: FormGroup = new FormGroup({});
  hide = true;
  private MAX_SCORE = 6;
  private apiService = inject(ApiService);
  private fb = inject(FormBuilder);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private auth = inject(AuthService);
  private location = inject(Location);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private executionErrorsSubscription: Subscription | undefined;
  recaptchaError: string | null = null;
  selectedPlan: SelectedPlanInfo | null = null;

  ngOnInit(): void {
    this.loadSelectedPlan();
    this.registerForm = this.fb.group({
      firstName: [
        '',
        [
          Validators.required,
          // Allow letters and spaces; allow a single apostrophe (') or backtick (`)
          // only once, not at the start or end, and it must be surrounded by letters
          Validators.pattern(
            /^(?=^[A-Za-z].*[A-Za-z]$)(?!.*['`].*['`])(?!.*(?:[^A-Za-z]['`]|['`][^A-Za-z]))[A-Za-z'` ]+$/
          ),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(?=^[A-Za-z].*[A-Za-z]$)(?!.*['`].*['`])(?!.*(?:[^A-Za-z]['`]|['`][^A-Za-z]))[A-Za-z'` ]+$/
          ),
        ],
      ],
      email: [
        '',
        [
          Validators.required,
         
          // Require a dot in the domain and a TLD of at least 2 letters
          Validators.pattern(
            /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
          ),
          Validators.minLength(8),
          Validators.maxLength(128),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(16),
          noWhitespaceOnlyValidator,
        ],
      ],
      termsStatus: ['', [Validators.requiredTrue]],
    });
    this.executionErrorsSubscription =
      this.recaptchaV3Service.onExecuteError.subscribe((data) => {
        this.recaptchaError = data ? String(data) : 'Unknown reCAPTCHA error';
      });

    // Try a single initial token fetch (primary verification happens on submit)
    this.recaptchaV3Service.execute('register').subscribe(
      (token) => {
        if (token) {
          this.captchaToken = token;
          //console.log(token);
          this.recaptchaError = null;
        }
      },
      (error) => {
        this.recaptchaError = error ? String(error) : 'reCAPTCHA load failed';
      }
    );
  }

  private loadSelectedPlan(): void {
    const raw = sessionStorage.getItem(SELECTED_PLAN_KEY);
    if (!raw) {
      this.selectedPlan = null;
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.planName) {
        this.selectedPlan = null;
        return;
      }
      this.selectedPlan = {
        planName: parsed.planName,
        billingTerm: parsed.billingTerm ?? 'monthly',
        pricePerMonth: Number(parsed.pricePerMonth) || 0,
        currencySymbol: parsed.currencySymbol ?? '€',
        quotaCalls: parsed.quotaCalls != null ? Number(parsed.quotaCalls) : null,
      };
    } catch {
      this.selectedPlan = null;
    }
  }

  private executeRecaptcha(action: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.recaptchaV3Service.execute(action).subscribe({
        next: (token) => {
          if (!token) {
            this.recaptchaError = 'Empty reCAPTCHA token';
            reject(new Error('Empty reCAPTCHA token'));
            return;
          }
          resolve(token);
        },
        error: (err) => reject(err),
      });
    });
  }

  ngOnDestroy(): void {
    if (this.executionErrorsSubscription) {
      this.executionErrorsSubscription.unsubscribe();
    }
  }

  getScore(): number {
    const pw = this.registerForm.get('password')?.value || '';
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score += 1;
    if (pw.length >= 12) score += 1;
    if (/[a-z]/.test(pw)) score += 1;
    if (/[A-Z]/.test(pw)) score += 1;
    if (/[0-9]/.test(pw)) score += 1;
    if (/[^A-Za-z0-9]/.test(pw)) score += 1;
    return Math.min(score, this.MAX_SCORE);
  }

  get strengthLabel(): Strength {
    const score = this.getScore();
    if (score === 0) return 'Empty';
    if (score <= 2) return 'Weak';
    if (score === 3) return 'Fair';
    if (score <= 5) return 'Good';
    return 'Strong';
  }

  get isPasswordTooWeak(): boolean {
    return this.strengthLabel === 'Weak';
  }

  get progressPercent(): number {
    return Math.round((this.getScore() / this.MAX_SCORE) * 100);
  }

  get progressClass(): string {
    const s = this.strengthLabel;
    switch (s) {
      case 'Weak':
        return 'weak';
      case 'Fair':
        return 'fair';
      case 'Good':
        return 'good';
      case 'Strong':
        return 'strong';
      default:
        return 'empty';
    }
  }

  toggleVisibility() {
    this.hide = !this.hide;
  }

  login() {
    this.auth.login();
  }
  isFieldInvalid(fieldName: string): boolean {
    const control = this.registerForm.get(fieldName);
    if (!control) {
      return false;
    }
    return control.invalid && (control.dirty || control.touched);
  }

  getPasswordErrorMessage(): string {
    const control = this.registerForm.get('password');
    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'Password is required.';
    }
    if (control.errors['whitespaceOnly']) {
      return 'Password cannot contain only spaces.';
    }
    if (control.errors['minlength']) {
      return 'Password must be at least 8 characters long.';
    }
    if (control.errors['maxlength']) {
      return 'Password can not be more than 16 characters long.';
    }

    return '';
  }
  async onSubmit() {
    // this.registerForm.value.captchaToken = this.captchaToken;

    try {
      this.captchaToken = await this.executeRecaptcha('register');
    } catch (e) {
      this.captchaToken = null;
      alert('reCAPTCHA verification failed. Please try again.');
      return;
    }

    const payload = {
      firstName: this.registerForm.value.firstName,
      lastName: this.registerForm.value.lastName,
      email: this.registerForm.value.email,
      password: this.registerForm.value.password,
      captchaToken: this.captchaToken,
      termsStatus: this.registerForm.value.termsStatus,
    };

    // payload prepared and sent to API

    this.apiService.post(ApiUrls.Public.REGISTER, payload).subscribe({
      next: (response) => {
        if (response.success) {
          localStorage.setItem(
            'user',
            JSON.stringify({
              ...response.data,
              password: this.registerForm.value.password,
            })
          );
          this.registerForm.reset();
          this.toastr.success('OTP sent successfully', 'Success');
          this.router.navigate(['/verify']);
        } else {
          this.toastr.error(response.message, 'Error');
        }
      },
      error: (err) => {
        this.toastr.error(err.error.message || 'Bad Request', 'Error');
      },
    });
  }

  get recaptchaVerified(): boolean {
    return !!this.captchaToken;
  }

  goBack() {
    this.location.back();
  }

}
